<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PostSaleStatusDeedForm from './PostSaleStatusDeedForm.vue';
import PostSaleDeedFeedbackChoiceGroup from './PostSaleDeedFeedbackChoiceGroup.vue';
import PostSaleDeedFeedbackProof from './PostSaleDeedFeedbackProof.vue';
import PostSaleDeedFeedbackReasonField from './PostSaleDeedFeedbackReasonField.vue';

const props = defineProps({
  form: { type: Object, required: true },
  paymentQuestion: { type: Object, default: () => ({ answers: [] }) },
  process: { type: Object, required: true },
  proof: { type: Object, default: null },
  resultQuestion: { type: Object, default: () => ({ answers: [] }) },
  uploading: { type: Boolean, default: false },
});
const emit = defineEmits(['update:form', 'upload']);
const { t } = useI18n();
const hasPayment = computed(() => Number(props.process.pg_na_escritura) === 1);
const update = (key, value) =>
  emit('update:form', { ...props.form, [key]: value });
const updateDate = value =>
  update(
    'nova_data',
    value
      .replace(/\D/g, '')
      .slice(0, 8)
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\d{2})\/(\d{2})(\d)/, '$1/$2/$3')
  );
</script>

<template>
  <section
    v-if="form.aconteceu === '1' && hasPayment"
    data-testid="post-sale-deed-feedback-payment-branch"
    class="mt-11"
  >
    <p class="mb-10 text-base leading-[22px] text-lais-text">
      {{ paymentQuestion.prompt
      }}{{ t('POST_SALE.DEED_FEEDBACK.REQUIRED_SUFFIX') }}
    </p>
    <PostSaleDeedFeedbackChoiceGroup
      :model-value="form.pagamento"
      :options="paymentQuestion.answers"
      @update:model-value="update('pagamento', $event)"
    />
    <div
      v-if="form.pagamento === '3'"
      data-testid="post-sale-deed-feedback-reschedule-branch"
      class="mt-11"
    >
      <p class="mb-10 text-base leading-[22px] text-lais-text">
        {{ t('POST_SALE.DEED_FEEDBACK.PAYMENT_RESCHEDULE.INSTRUCTION') }}
      </p>
      <label
        class="mb-8 grid w-full max-w-[225px] gap-1.5 text-[13px] font-semibold text-lais-text-body"
      >
        <span>
          {{ t('POST_SALE.DEED_FEEDBACK.PAYMENT_RESCHEDULE.DATE_LABEL')
          }}{{ t('POST_SALE.DEED_FEEDBACK.REQUIRED_SUFFIX') }}
        </span>
        <input
          :value="form.nova_data"
          maxlength="10"
          inputmode="numeric"
          :placeholder="
            t('POST_SALE.DEED_FEEDBACK.PAYMENT_RESCHEDULE.DATE_PLACEHOLDER')
          "
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-base font-normal text-lais-text-body outline-none transition-colors placeholder:text-lais-text-muted focus:border-lais-primary"
          @input="updateDate($event.target.value)"
        />
      </label>
      <PostSaleDeedFeedbackReasonField
        :label="t('POST_SALE.DEED_FEEDBACK.PAYMENT_RESCHEDULE.REASON_LABEL')"
        :model-value="form.motivo_pagamento"
        :placeholder="
          t('POST_SALE.DEED_FEEDBACK.PAYMENT_RESCHEDULE.REASON_PLACEHOLDER')
        "
        @update:model-value="update('motivo_pagamento', $event)"
      />
    </div>
    <PostSaleDeedFeedbackProof
      v-if="form.pagamento === '4'"
      data-testid="post-sale-deed-feedback-proof-branch"
      class="mt-11"
      :artifact="proof"
      :loading="uploading"
      @upload="emit('upload', $event)"
    />
  </section>

  <section
    v-if="form.aconteceu === '2'"
    data-testid="post-sale-deed-feedback-result-branch"
    class="mt-11"
  >
    <p class="mb-10 text-base leading-[22px] text-lais-text">
      {{ resultQuestion.prompt
      }}{{ t('POST_SALE.DEED_FEEDBACK.REQUIRED_SUFFIX') }}
    </p>
    <PostSaleDeedFeedbackChoiceGroup
      :model-value="form.resultado"
      :options="resultQuestion.answers"
      @update:model-value="update('resultado', $event)"
    />
    <div
      v-if="form.resultado === '5'"
      data-testid="post-sale-deed-feedback-fall-branch"
      class="mt-11"
    >
      <p class="mb-10 text-base leading-[22px] text-lais-text">
        {{ t('POST_SALE.DEED_FEEDBACK.FALL.INSTRUCTION') }}
      </p>
      <PostSaleDeedFeedbackReasonField
        :label="t('POST_SALE.DEED_FEEDBACK.FALL.REASON_LABEL')"
        :model-value="form.motivo_queda"
        :placeholder="t('POST_SALE.DEED_FEEDBACK.FALL.REASON_PLACEHOLDER')"
        @update:model-value="update('motivo_queda', $event)"
      />
    </div>
    <PostSaleStatusDeedForm
      v-if="form.resultado === '6'"
      data-testid="post-sale-deed-feedback-new-deed-branch"
      class="mt-11"
      :catalog="process.deed_catalog"
      :post-sale-case-id="process.processo_id"
      :value="form.deed"
      @update:value="update('deed', $event)"
    />
  </section>
</template>
